import {
  Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  doneIcon, editIcon, EventPayload, minusIcon, ModalService, RestApiService, SecureFilePipe, shareIcon, UIKitIconRegistry,
} from '@ui-kit';
// eslint-disable-next-line import/no-unresolved
import { P } from '@ui-kit/ui-kit';
import { get, set } from 'lodash-es';
import { UserService } from '../../service';

@Component({
  selector: 'app-loan-application-file',
  templateUrl: './loan-application-file.component.html',
  styleUrls: [
    './loan-application-file.component.scss',
  ],
})
export class LoanApplicationFileComponent implements OnInit {
  static EDIT_MODAL_ID = `${LoanApplicationFileComponent.name}-edit`;

  @ViewChild('hiddenUrl', { static: true }) hiddenUrl: ElementRef<HTMLAnchorElement> | undefined;

  @ViewChild('editRef', { static: true }) editRef: TemplateRef<unknown> | undefined;

  editForm: FormGroup = this.formBuilder.group({
    path: [
      { value: '', disabled: false },
      Validators.compose([Validators.required]),
    ],
    key: [
      { value: '', disabled: false },
      Validators.compose([Validators.required]),
    ],
    fileName: [
      { value: '', disabled: false },
      Validators.compose([Validators.required]),
    ],
    createdAt: [
      { value: '', disabled: false },
      Validators.compose([Validators.required]),
    ],
  });

  editFile: P.LoanFile | undefined;

  @Input() application: P.Application | P.LoanApplication | undefined;

  @Input() title: string = 'Files(s)';

  @Input() path: string = '';

  @Input() uploadTarget: 'application' | 'loan' = 'loan';

  @Output() update: EventEmitter<P.Application | P.LoanApplication> = new EventEmitter();

  get files(): P.LoanFile[] {
    const files = get(this.application, this.path);

    return (Array.isArray(files)
      ? files
      : files
        ? [files]
        : []).filter((file) => !file.invalid);
  }

  working: boolean | undefined;

  constructor(
    private user: UserService,
    private formBuilder: FormBuilder,
    private modal: ModalService,
    private restApi: RestApiService,
    private secureFile: SecureFilePipe,
    iconRegistry: UIKitIconRegistry,
  ) {
    iconRegistry.registerIcons([
      minusIcon,
      doneIcon,
      editIcon,
      shareIcon,
    ]);
  }

  get userId() {
    return this.user.current?.id;
  }

  ngOnInit(): void {

  }

  viewFile(file: P.LoanFile, event?: MouseEvent | TouchEvent): void {
    this.working = true;

    this.restApi
      .getFile(file.key)
      .toPromise()
      .then((blob) => window.open(URL.createObjectURL(blob)))
      .catch((error) => this.modal.open(error?.message || 'Oops', { id: 'error-modal' }))
      .finally(() => this.working = false);
  }

  downloadFile(file: P.LoanFile): void {
    if (!file || !file?.key) {
      return;
    }

    this.secureFile
      .transform(file.key)
      .toPromise()
      .then((dataUri) => {
        const aElement: HTMLAnchorElement = this.hiddenUrl?.nativeElement as HTMLAnchorElement;
        aElement.download = `${file.fileName}`;
        aElement.href = dataUri as unknown as string;
        aElement.click();
      })
      .catch((error) => this.modal
        .open(error?.message || 'Oops', { id: 'error-modal' }));
  }

  renameFile(file: P.LoanFile): void { }

  toggleValidation(file: P.LoanFile): void {
    if (!file || !file?.key) {
      return;
    }

    let files: P.LoanFile[] | P.LoanFile | null = get(this.application, this.path);

    if (Array.isArray(files)) {
      //
      const i = files
        .map((f: P.LoanFile) => f.key)
        .indexOf(file.key);

      if (i > -1) {
        const file = files[i];
        files[i] = {
          ...file,
          invalid: !file.invalid,
        };
      }
    } else {
      files = null;
    }

    const shallowPath = this.path
      .split('.')
      .filter((key) => !!key)[0];

    const base = shallowPath
      ? { [shallowPath]: get(this.application, shallowPath) }
      : {};

    const update = set(base, this.path, files);
    this.working = true;

    this.restApi
      .patchIntermediaryApplication(this.application?.id || '', update)
      .toPromise()
      .then((loanApplication) => this.update.emit(loanApplication))
      .catch((error) => this.modal.open(error?.message || 'Oops', { id: 'error-modal' }))
      .finally(() => this.working = false);
  }

  openEditFile(file: P.LoanFile, event?: MouseEvent | TouchEvent, path: string = this.path): void {
    if (!file || !file?.key || !path || !this.editRef) {
      return;
    }

    this.editFile = file;

    const createdAt = file?.createdAt
      ? new Date(file.createdAt).toISOString().substring(0, 10)
      : '';

    this.editForm.setValue({
      path,
      key: file.key,
      fileName: file.fileName,
      createdAt,
    });

    this.modal.close(LoanApplicationFileComponent.EDIT_MODAL_ID);
    this.modal
      .open(this.editRef, { id: LoanApplicationFileComponent.EDIT_MODAL_ID, nav: false });
  }

  submitEditFile(event: Event): void {
    const value = this.editForm.value;

    this.modal.close(LoanApplicationFileComponent.EDIT_MODAL_ID);

    this.editForm.reset();
    this.editFile = void 0;

    let files: P.LoanFile[] | P.LoanFile | null = get(this.application, this.path);

    if (!files) { return; }

    if (Array.isArray(files)) {
      //
      const i = files
        .map((f: P.LoanFile) => f.key)
        .indexOf(value.key);

      let file = files[i];

      if (!file) { return; }

      file = {
        ...file,
        fileName: value.fileName,
        createdAt: value.createdAt,
      };

      files[i] = file;

      if (!file) { return; }
    } else {
      files = {
        ...files,
        fileName: value.fileName,
        createdAt: value.createdAt,
      } as P.LoanFile;
    }

    const shallowPath = this.path
      .split('.')
      .filter((key) => !!key)[0];

    const base = shallowPath
      ? { [shallowPath]: get(this.application, shallowPath) }
      : {};

    const update = set(base, this.path, files);
    this.working = true;

    this.restApi
      .patchIntermediaryApplication(this.application?.id || '', update)
      .toPromise()
      .then((loanApplication) => this.update.emit(loanApplication))
      .catch((error) => this.modal.open(error?.message || 'Oops', { id: 'error-modal' }))
      .finally(() => this.working = false);
  }

  deleteFile(file: P.LoanFile, event?: MouseEvent | TouchEvent): void {
    if (!file || !file?.key) {
      return;
    }

    this.modal
      .open('Are you sure?', { ok: 'Yes', cancel: 'Cancel' })
      .then((response) => {
        if (response.action === 'ok') {
          let files: P.LoanFile[] | P.LoanFile | null = get(this.application, this.path);

          if (Array.isArray(files)) {
            //
            const i = files
              .map((f: P.LoanFile) => f.key)
              .indexOf(file.key);

            if (i > -1) {
              files.splice(i, 1);
            }
          } else {
            files = null;
          }

          const shallowPath = this.path
            .split('.')
            .filter((key) => !!key)[0];

          const base = shallowPath
            ? { [shallowPath]: get(this.application, shallowPath) }
            : {};

          const update = set(base, this.path, files);
          this.working = true;

          this.restApi
            .patchIntermediaryApplication(this.application?.id || '', update)
            .toPromise()
            .then((loanApplication) => this.update.emit(loanApplication))
            .catch((error) => this.modal.open(error?.message || 'Oops', { id: 'error-modal' }))
            .finally(() => this.working = false);
        }
      });
  }

  onSuccessUpload(event: EventPayload) {
    this.update.emit(this.uploadTarget === 'loan' ? event.loan : event.application);
  }
}
